import { Card, Stack, Row, Col, Form } from "react-bootstrap";
import { useState } from "react";
import { AppIcon } from "@/components/Icon";

import {
  downloadSOAReport,
  downloadRepaymentScheduleReport,
} from "@/services/loanservices";

import {
  getAgreementsRequest
} from "@/services/agreements";

export default function ReportsMainCard({ loanUUID }) {
  const [activeTab, setActiveTab] = useState("reports");
  const [reportType, setReportType] = useState("soa");
  const [soaFormat, setSoaFormat] = useState("xlsx");
  const [documentType, setDocumentType] = useState("personal_guarantee");
  const [loading, setLoading] = useState(false);

  const handleDownloadPDF = async () => {
    try {
      setLoading(true);

      // REPORTS TAB
      if (activeTab === "reports") {
        if (reportType === "soa") {
          const response = await downloadSOAReport(loanUUID, soaFormat);
          const downloadUrl = response?.results?.download_url;
          if (!downloadUrl) throw new Error("Download URL not found");
          if (soaFormat === "pdf") {
            window.open(downloadUrl, "_blank");
          } else {
            window.location.href = downloadUrl;
          }
        } else {
          const blob = await downloadRepaymentScheduleReport({ loan_uuid: loanUUID });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "Repayment_Schedule.pdf";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      }

      // DOCUMENTS TAB
      if (activeTab === "documents") {
        if (!loanUUID) {
          throw new Error("Loan UUID not available");
        }

        const response = await getAgreementsRequest({
          loan_id: loanUUID,
          agreement_type: documentType,
        });

        const fileUrl = response?.results?.[0]?.signed_document_url

        if (!fileUrl) {
          throw new Error("Signed URL not found");
        }

        // Direct S3 download
        const link = document.createElement("a");
        link.href = fileUrl;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (error) {
      console.error("Download failed:", error);
      alert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 rounded-lg">
      <Card.Body>
        <Stack gap={4}>

          <Stack gap={4}>
            <Stack direction="horizontal" gap={3}>
              <div className="bg-info-subtle rounded-circle p-3 d-flex align-items-center justify-content-center">
                <AppIcon name="DocumentDuplicate" color="#0B8085" size="30" />
              </div>
            </Stack>

            <Stack gap={1}>
              <h5 className="fw-semibold mb-1">
                Download Reports & Documents
              </h5>
              <small className="text-muted">
                You can select from (Statement of Account (SOA), Repayment Schedule,
                Forfaiting Agreement or Personal Guarantee Documents)
              </small>
            </Stack>
          </Stack>

          {/* Tabs */}
          <Stack direction="horizontal" gap={2}>
            <button
              onClick={() => setActiveTab("reports")}
              className={`btn rounded-pill px-3 py-2 ${activeTab === "reports"
                ? "btn-dark"
                : "btn-light border"
                }`}
            >
              Reports
            </button>

            <button
              onClick={() => setActiveTab("documents")}
              className={`btn rounded-pill px-3 py-2 ${activeTab === "documents"
                ? "btn-dark"
                : "btn-light border"
                }`}
            >
              Documents
            </button>
          </Stack>

          {/* REPORTS TAB */}
          {activeTab === "reports" && (
            <>
              <Stack gap={2}>
                <small className="fw-medium">Type of report</small>

                <Row>
                  <Col xs={12} md={5} lg={3}>
                    <Form.Select
                      className="rounded-3 py-3"
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                    >
                      <option value="soa">
                        Statement of Account (SOA)
                      </option>
                      <option value="repayment">
                        Repayment Schedule
                      </option>
                    </Form.Select>
                  </Col>
                </Row>
              </Stack>

              {reportType === "soa" && (
                <Stack gap={2}>
                  <small className="fw-medium">Format</small>
                  <Stack direction="horizontal" gap={3}>
                    <Form.Check
                      type="radio"
                      id="soa-format-xlsx"
                      label="Excel (.xlsx)"
                      value="xlsx"
                      checked={soaFormat === "xlsx"}
                      onChange={() => setSoaFormat("xlsx")}
                    />
                    <Form.Check
                      type="radio"
                      id="soa-format-pdf"
                      label="PDF (.pdf)"
                      value="pdf"
                      checked={soaFormat === "pdf"}
                      onChange={() => setSoaFormat("pdf")}
                    />
                  </Stack>
                </Stack>
              )}

              <hr className="my-2 border-primary-light" />

              <Stack direction="horizontal" gap={3}>
                <button
                  onClick={handleDownloadPDF}
                  disabled={loading}
                  className="btn btn-light rounded-4 px-4 py-3 d-flex align-items-center"
                >
                  <AppIcon
                    name={reportType === "soa" && soaFormat === "xlsx" ? "Xls" : "Pdf"}
                    size={20}
                    color={reportType === "soa" && soaFormat === "xlsx" ? "#1D6F42" : "#FF1607"}
                    cx="me-2"
                  />
                  {loading
                    ? "Downloading..."
                    : reportType === "soa" && soaFormat === "xlsx"
                      ? "Download as Excel"
                      : "Download as PDF"}
                </button>
              </Stack>
            </>
          )}

          {/* DOCUMENTS TAB */}
          {activeTab === "documents" && (
            <>
              <Stack gap={2}>
                <small className="fw-medium">Select a document</small>

                <Row>
                  <Col xs={12} md={5} lg={3}>
                    <Form.Select
                      className="rounded-3 py-3"
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                    >
                      <option value="personal_guarantee">
                        Personal Guarantee
                      </option>
                      <option value="forfaiting_agreement">
                        Forfaiting Agreement
                      </option>
                    </Form.Select>
                  </Col>
                </Row>
              </Stack>

              <hr className="my-2 border-primary-light" />

              <Stack direction="horizontal" gap={3}>
                <button
                  onClick={handleDownloadPDF}
                  disabled={loading}
                  className="btn btn-light rounded-4 px-4 py-3 d-flex align-items-center"
                >
                  <AppIcon name="Pdf" size={20} color="#FF1607" cx="me-2" />
                  {loading ? "Downloading..." : "Download as PDF"}
                </button>
              </Stack>
            </>
          )}

        </Stack>
      </Card.Body>
    </Card>
  );
}
